import { DeviceError, isTransportError } from './device-error';

const CB_DEBUG = { ENABLED: true };
const cbLog = (label: string, msg: string) => CB_DEBUG.ENABLED && console.log(`[CircuitBreaker:${label}] ${msg}`);

// ─── Circuit Breaker ──────────────────────────────────────────────────────────
//
// State machine with three states:
//   CLOSED    → normal operation, requests pass through
//   OPEN      → channel is dead, requests are rejected immediately
//   HALF_OPEN → testing recovery, one request allowed through
//
// Only TransportErrors trip the breaker. ProtocolErrors (Modbus exceptions)
// indicate the channel works fine — the register just doesn't exist.
//
// Integration: the scheduler calls onSuccess()/onFailure() directly —
// onFailure is called ONCE per logical operation (after the retry loop),
// not per attempt, to avoid premature circuit opening during transient failures.
// The CB has no execute() method; the scheduler controls the flow and checks
// circuitBreaker.state before retries and in addConcurrentRequest.

export type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

export interface CircuitBreakerConfig {
  /** Number of consecutive TransportErrors before opening the circuit. Default: 3 */
  failureThreshold: number;
  /** Time in ms before transitioning from OPEN → HALF_OPEN. Default: 30000 (30s) */
  resetTimeoutMs: number;
  /** Optional predicate to filter which errors should trip the breaker.
   *  Defaults to: only TransportErrors trip. */
  shouldTrip?: (error: DeviceError) => boolean;
}

const DEFAULT_CONFIG: CircuitBreakerConfig = {
  failureThreshold: 3,
  resetTimeoutMs: 30_000,
};

export class CircuitBreaker {
  private _state: CircuitState = 'CLOSED';
  private consecutiveFailures = 0;
  private lastFailureTime = 0;
  private readonly config: CircuitBreakerConfig;
  private readonly label: string;

  constructor(label: string, config?: Partial<CircuitBreakerConfig>) {
    this.label = label;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  get state(): CircuitState {
    // Auto-transition OPEN → HALF_OPEN when resetTimeout elapsed
    if (this._state === 'OPEN' && this.resetTimeoutElapsed()) {
      this.transitionTo('HALF_OPEN');
    }
    return this._state;
  }

  /** Record a successful operation. Resets failure count, closes circuit. */
  onSuccess(): void {
    if (this._state === 'HALF_OPEN') {
      cbLog(this.label, 'HALF_OPEN → CLOSED (success)');
    }
    this.consecutiveFailures = 0;
    this.transitionTo('CLOSED');
  }

  /** Record a failed operation. Opens circuit if threshold exceeded. */
  onFailure(error: DeviceError): void {
    const shouldTrip = this.config.shouldTrip
      ? this.config.shouldTrip(error)
      : isTransportError(error);

    if (!shouldTrip) {
      // Protocol errors don't count toward circuit failure
      return;
    }

    this.consecutiveFailures++;
    this.lastFailureTime = Date.now();

    if (this._state === 'HALF_OPEN') {
      cbLog(this.label, `HALF_OPEN → OPEN (test request failed: ${error.kind})`);
      this.transitionTo('OPEN');
      return;
    }

    if (this.consecutiveFailures >= this.config.failureThreshold) {
      cbLog(this.label, `CLOSED → OPEN (${this.consecutiveFailures} consecutive failures)`);
      this.transitionTo('OPEN');
    }
  }

  /** Reset the circuit breaker to CLOSED state. Use when channel is explicitly reconnected. */
  reset(): void {
    cbLog(this.label, 'reset → CLOSED');
    this.consecutiveFailures = 0;
    this.lastFailureTime = 0;
    this._state = 'CLOSED';
  }

  private transitionTo(newState: CircuitState): void {
    if (this._state !== newState) {
      this._state = newState;
      if (newState === 'HALF_OPEN') {
        cbLog(this.label, 'OPEN → HALF_OPEN (resetTimeout elapsed)');
      }
    }
  }

  private resetTimeoutElapsed(): boolean {
    return Date.now() - this.lastFailureTime >= this.config.resetTimeoutMs;
  }
}
